import React, { useEffect, useState } from 'react'
import i18n from '~/i18n'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import ButtonSimple from '~/Common-Objects/UI/Forms/ButtonSimple/ButtonSimple'
import Column from '~/Common-Objects/Grids/Column/Column'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Row from '~/Common-Objects/Grids/Row/Row'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import axios from '~/data/http/axios'
import Theme from '~/pages/cp/templates/add/dragDropV2/themes/theme'

function pickLayout(onSelect, type, item) {
    if (typeof onSelect !== 'function' || !item || item.id == null) return
    onSelect({ type, id: item.id, item })
}

function tabLabel(kind, tabId) {
    if (tabId === 'themes') {
        return kind === 'landing' ? i18n.t('templates:add.magicTools.themes') : i18n.t('templates:add.themes')
    }
    if (tabId === 'prompt') return i18n.t('templates:add.magicTools.describeStyle')
    return kind === 'landing' ? i18n.t('templates:add.magicTools.alreadyUsed') : i18n.t('templates:add.savedTemplates')
}

export default function LookAndFeelPicker({
    kind = 'email',
    showNoStyle = false,
    selected,
    onSelect,
    prompt,
    onPromptChange,
    onUsePrompt
}) {
    const [tab, setTab] = useState(kind === 'landing' ? 'saved' : 'themes')
    const [themes, setThemes] = useState([])
    const [saved, setSaved] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                if (kind === 'landing') {
                    const [themesRes, pagesRes] = await Promise.all([
                        axios.get('/theme?type=10'),
                        axios.get('/landingPage?limit=6&orderBy=lastUpdatedAt&orderDir=desc')
                    ])
                    if (cancelled) return
                    setThemes(themesRes.data?.Themes || [])
                    setSaved((pagesRes.data?.landingPages || []).slice(0, 6))
                    return
                }

                const [groupsRes, templatesRes] = await Promise.all([
                    axios.get('/themegroup'),
                    axios.get('/template?recordLimit=24&startRecord=0&sortColumn=id&sortOrder=desc')
                ])
                if (cancelled) return
                const groups = groupsRes.data?.ThemeGroups || []
                const featured = groups.find((group) => group.name === 'Featured') || groups[0]
                let themeRows = []
                if (featured?.id) {
                    const themesRes = await axios.get(
                        `/themegroup/${featured.id}/themes?type=1&orderBy=createdAt&orderDir=desc&limit=24&offset=0`
                    )
                    themeRows = themesRes.data?.Themes || []
                } else {
                    const themesRes = await axios.get('/theme?type=1&orderBy=createdAt&orderDir=desc&limit=24&offset=0')
                    themeRows = themesRes.data?.Themes || []
                }
                if (cancelled) return
                setThemes(themeRows)
                setSaved(templatesRes.data?.Templates || [])
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [kind])

    const selectedKey = selected ? `${selected.type}:${selected.id}` : ''
    const savedType = 'template'
    const items = tab === 'themes' ? themes : saved
    const itemType = tab === 'themes' ? 'theme' : savedType
    const tabs = kind === 'landing' ? ['saved', 'themes'] : ['themes', 'saved']
    if (typeof onUsePrompt === 'function') tabs.push('prompt')

    return (
        <div>
            {showNoStyle && tab !== 'prompt' ? (
                <div className='mar-b20'>
                    <Button
                        className='button-primary'
                        text={i18n.t('templates:add.magicTools.noStyles')}
                        onClick={() => onSelect({ type: 'none' })}
                        data-test='ai-create-no-style'
                    />
                    <p className='mar-t25 mar-b0'>{i18n.t('templates:add.magicTools.orUseExistingTheme')}</p>
                </div>
            ) : null}
            <div className='mar-b20'>
                {tabs.map((tabId, index, order) => (
                    <span key={tabId} data-test={`ai-create-look-tab-${tabId}`}>
                        <ButtonSimple
                            className={
                                index < order.length - 1 ? 'button-simple-fullsize mar-r5' : 'button-simple-fullsize'
                            }
                            selected={tab === tabId}
                            onClick={() => setTab(tabId)}
                        >
                            {tabLabel(kind, tabId)}
                        </ButtonSimple>
                    </span>
                ))}
            </div>
            {tab === 'prompt' ? (
                <div data-test='ai-create-generate-with-ai'>
                    <p className='mar-b15'>{i18n.t('templates:add.magicTools.generateWithAiSubtitle')}</p>
                    <TextArea
                        name='aiPrompt'
                        rows={4}
                        value={prompt || ''}
                        onChange={(ev) => onPromptChange(ev.target.value)}
                        placeholder={i18n.t('templates:add.magicTools.generateWithAiPlaceholder')}
                    />
                    <Button
                        className='mar-t15'
                        text={i18n.t('templates:add.magicTools.useAiPrompt')}
                        disabled={!(prompt || '').trim()}
                        onClick={onUsePrompt}
                    />
                </div>
            ) : null}
            {tab !== 'prompt' && loading ? <LoadingIcons iconType='2' /> : null}
            {tab !== 'prompt' && !loading ? (
                <Row>
                    {items.map((item) => {
                        const id = item.id
                        return (
                            <Column xs={12} sm={6} md={4} key={`${itemType}-${id}`}>
                                <Theme
                                    theme={{
                                        ...item,
                                        name: item.name || item.templateName || item.formName,
                                        type: item.type || (kind === 'landing' ? 10 : 1)
                                    }}
                                    selected={selectedKey === `${itemType}:${id}`}
                                    select={() => pickLayout(onSelect, itemType, item)}
                                    disablePreview={true}
                                />
                            </Column>
                        )
                    })}
                </Row>
            ) : null}
        </div>
    )
}
